import { useState } from "react";
import { ApiError, updateOccupant, type OccupantPatch } from "../api/webcheckinApi";
import type { WebcheckinDocumentType } from "../types";
import { useWizardContext } from "./shared";
import { CountryPicker } from "../components/CountryPicker";
import { PhoneInput } from "../components/PhoneInput";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function RegisterScreen({ token }: { token: string }) {
  const { state, dispatch } = useWizardContext();
  const primary = state.payload!.session.occupants.find((o) => o.role === "primary");
  if (!primary) return null;

  /*
   * NO PRE-FILLED FIELDS FROM A PHOTOGRAPH.
   *
   * These used to fall back to what had been read off the uploaded document.
   * Nothing writes that any more, so the fallbacks were code that could only
   * ever read null — the guest types their own details, which is the whole
   * point of not holding an image of the document.
   */
  const [firstName, setFirstName] = useState(primary.firstName || "");
  const [lastName, setLastName]   = useState(primary.lastName || "");
  const [dateOfBirth, setDob]     = useState(primary.dateOfBirth || "");
  const [nationality, setNat]     = useState(primary.nationality || "");
  /* The guest states the document type here. It used to be owned by the photo
     step, which no longer exists — see the note beside the control below. */
  const [documentType, setDocType] = useState<WebcheckinDocumentType>(
    (primary.documentType || "passport") as WebcheckinDocumentType
  );
  const [documentNumber, setDocNum] = useState(primary.documentNumber || "");
  const [phone, setPhone] = useState<string | null>(primary.phoneE164 ?? null);
  const email = primary.email ?? "";

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valid = firstName.trim() && lastName.trim() && dateOfBirth && nationality && documentNumber;
  const missing = [
    !firstName.trim() && "first name",
    !lastName.trim() && "last name",
    !dateOfBirth && "date of birth",
    !nationality && "nationality",
    !documentNumber && "document number",
  ].filter(Boolean) as string[];

  function buildPatch(): OccupantPatch {
    return {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      dateOfBirth: dateOfBirth || null,
      nationality: nationality || null,
      documentType,
      documentNumber: documentNumber || null,
      phoneE164: phone,
      // Persist the prefilled booking email into the occupant column so it
      // shows up on the operator's submitted-data card (otherwise only the
      // public-read getSession fallback would surface it).
      email: email.trim() || null,
    };
  }

  async function handleContinue() {
    if (!valid || !primary) return;
    setBusy(true);
    setError(null);
    try {
      await updateOccupant(token, primary.id, buildPatch());
      dispatch({ type: "next" });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setBusy(false);
    }
  }


  const currentYear = new Date().getFullYear();

  return (
    <div>
      <h1 className="screen-h1">Your details</h1>
      <p className="screen-p mt-2">Please check the details below and correct anything that's wrong.</p>

      <SectionHeading>Identity</SectionHeading>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="First name *" htmlFor="first">
          <Input id="first" value={firstName} onChange={(e) => setFirstName(e.target.value)} autoComplete="given-name" />
        </Field>
        <Field label="Last name *" htmlFor="last">
          <Input id="last" value={lastName} onChange={(e) => setLastName(e.target.value)} autoComplete="family-name" />
        </Field>
        <Field label="Date of birth *">
          <DatePicker
            value={dateOfBirth || null}
            onChange={(v) => setDob(v ?? "")}
            placeholder="Pick a date"
            fromYear={1925}
            toYear={currentYear}
          />
        </Field>
        <Field label="Nationality *">
          <CountrySelect value={nationality} onChange={setNat} />
        </Field>
      </div>

      <SectionHeading>Travel document</SectionHeading>
      {/*
        * THE TYPE IS CHOSEN HERE, NOT READ OFF A PHOTOGRAPH.
        *
        * This used to show whatever the upload screen had detected, with a
        * button back to it. Photographing an identity document is not
        * something a hotel may do (Roman, 2026-09-01), so the guest states
        * the type in one tap — the same fact, without an image of the
        * document existing anywhere.
        */}
      <div className="mb-3">
        <Field label="Document type *" htmlFor="doctype">
          <Select value={documentType} onValueChange={(v) => setDocType(v as WebcheckinDocumentType)}>
            <SelectTrigger id="doctype" className="cursor-pointer">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="passport" className="cursor-pointer">Passport</SelectItem>
              <SelectItem value="national_id" className="cursor-pointer">National ID</SelectItem>
              <SelectItem value="driver_licence" className="cursor-pointer">Driver&apos;s licence</SelectItem>
              <SelectItem value="other" className="cursor-pointer">Other</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
      <div className="grid grid-cols-1 gap-3">
        <Field label="Document number *" htmlFor="docnum">
          <Input id="docnum" value={documentNumber} onChange={(e) => setDocNum(e.target.value)} />
        </Field>
      </div>

      <SectionHeading>Contact</SectionHeading>
      <div className="grid grid-cols-1 gap-3">
        <Field
          label="Email"
          hint="The address we sent your check-in link to. Contact reception to change it."
        >
          <Input
            type="email"
            value={email}
            readOnly
            disabled
            className="bg-muted text-muted-foreground"
          />
        </Field>
        <Field label="Phone number" hint="Optional. Helpful for reception to reach you on arrival day.">
          <PhoneInput value={phone} onChange={setPhone} defaultDialIso2={nationality || "GR"} />
        </Field>
      </div>

      {error && <p className="mt-4 text-sm text-destructive">{error}</p>}
      {!valid && missing.length > 0 && (
        <p className="mt-4 text-xs text-amber-700">
          To continue, please complete: {missing.join(", ")}.
        </p>
      )}

      <Button className="mt-6 w-full h-11" disabled={!valid || busy} onClick={handleContinue}>
        {busy ? "Saving…" : "Continue"}
      </Button>
    </div>
  );
}


function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mt-6 mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      {children}
    </h2>
  );
}

function Field({
  label,
  hint,
  htmlFor,
  children,
}: {
  label: string;
  hint?: string;
  htmlFor?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function CountrySelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <CountryPicker
      value={value}
      onChange={onChange}
      placeholder="— Select —"
      triggerClassName="w-full"
    />
  );
}
